import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatCard, BarRow } from "@/components/leadsdash/DashBits";
import { Users, PhoneCall, Clock, AlertTriangle, CheckCircle2, ListChecks } from "lucide-react";

/** Starlink outbound campaign dashboard — per Type → Status funnel + capture breakdowns + agent completion. */
export default function CampaignTab({ filters, navigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = {};
    ["assignee", "date_from", "date_to"].forEach((k) => { if (filters?.[k]) params[k] = filters[k]; });
    api.get("/leads/analytics/campaign", { params }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [filters?.assignee, filters?.date_from, filters?.date_to]);

  if (!data) return <div className="text-xs text-slate-400 p-4">Loading campaign figures…</div>;
  const t = data.totals;
  const drill = (extra) => navigate(`/leads?${new URLSearchParams(extra).toString()}`);

  return (
    <div className="space-y-4" data-testid="campaign-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Leads" value={t.total} icon={ListChecks} testid="camp-kpi-total" onClick={() => navigate("/leads")} />
        <StatCard label="Called" value={t.called} icon={PhoneCall} accent="text-emerald-600" testid="camp-kpi-called" />
        <StatCard label="Pending" value={t.pending} icon={Clock} accent="text-amber-600" testid="camp-kpi-pending" />
        <StatCard label="Closed" value={t.closed} icon={CheckCircle2} accent="text-slate-600" testid="camp-kpi-closed" />
        <StatCard label="Callbacks due" value={t.callbacks_due} icon={Clock} accent="text-sky-600" testid="camp-kpi-cb-due"
                  onClick={() => drill({ disposition: "CB" })} />
        <StatCard label="Callbacks overdue" value={t.callbacks_overdue} icon={AlertTriangle} accent="text-rose-600" testid="camp-kpi-cb-overdue" />
      </div>

      <Card data-testid="campaign-buckets">
        <CardHeader className="pb-2"><CardTitle className="text-sm">By Type → Status</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[760px] text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-3 py-1.5">Type</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-right px-2 py-1.5">Total</th>
                <th className="text-right px-2 py-1.5">Called</th>
                <th className="text-right px-2 py-1.5">Pending</th>
                <th className="text-right px-2 py-1.5">Closed</th>
                <th className="text-right px-2 py-1.5">CB due</th>
                <th className="text-right px-2 py-1.5">CB overdue</th>
                <th className="text-right px-2 py-1.5">Progress</th>
              </tr>
            </thead>
            <tbody>
              {data.buckets.map((b) => (
                <tr key={`${b.type}-${b.status}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`camp-bucket-${b.status}`}
                    onClick={() => drill({ ff: JSON.stringify({ cf_lead_type: b.type, cf_lead_status: b.status }) })}>
                  <td className="px-3 py-1.5 font-medium text-slate-800">{b.type_label}</td>
                  <td className="px-2 py-1.5 text-slate-600">
                    <div className="max-w-[320px] truncate" title={b.status_label}>{b.status_header || b.status_label}</div>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{b.total}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{b.called}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-amber-700">{b.pending}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{b.closed}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-sky-700">{b.callbacks_due}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-rose-700">{b.callbacks_overdue}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{b.total ? Math.round((b.called / b.total) * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Breakdown title="Customer feedback" rows={data.customer_feedback} total={t.called} testid="camp-feedback" color="#059669" />
        <Breakdown title="Escalation threat" rows={data.escalation_threat} total={t.called} testid="camp-threat" color="#DC2626" />
        <Breakdown title="Customer tone" rows={data.customer_tone} total={t.called} testid="camp-tone" color="#D97706" />
        <Breakdown title="Language of call" rows={data.language_of_call} total={t.called} testid="camp-lang" color="#0284C7" />
        <Breakdown title="Final disposition" rows={data.disposition} total={t.called} testid="camp-dispo" color="#4F46E5" />
        <Card data-testid="camp-agents">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4 text-slate-500" /> Agent completion</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {data.agents.length === 0 && <div className="text-xs text-slate-400">No leads assigned yet.</div>}
            {data.agents.map((a) => (
              <div key={a.email} className="flex items-center justify-between gap-2 text-xs cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5"
                   onClick={() => drill({ assignee: a.email })} data-testid={`camp-agent-${a.email}`}>
                <span className="text-slate-700 w-1/3 shrink-0 break-words leading-tight" title={a.name}>{a.name}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full rounded bg-indigo-500" style={{ width: `${a.assigned ? Math.round((a.completed / a.assigned) * 100) : 0}%` }} />
                </div>
                <span className="font-mono text-slate-600">{a.completed} / {a.assigned}
                  <span className="text-slate-400"> · {a.assigned ? Math.round((a.completed / a.assigned) * 100) : 0}%</span></span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <DispositionGroupsCard groups={data.disposition_groups || []} called={t.called} drill={drill} />
    </div>
  );
}

const GROUP_COLORS = { P1: "#64748B", P2: "#D97706", P3: "#059669", P4: "#DC2626", __ungrouped__: "#94A3B8" };

function DispositionGroupsCard({ groups, called, drill }) {
  return (
    <Card data-testid="camp-dispo-groups">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Dispositions by group
          <span className="text-[10px] text-slate-400 font-normal ml-2">latest disposition per lead · {called} called</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {groups.map((g) => {
          const color = GROUP_COLORS[g.key] || "#6366F1";
          const empty = g.total === 0;
          return (
            <div key={g.key} className={empty ? "opacity-50" : ""} data-testid={`camp-group-${g.key}`}>
              <div className={`flex items-center gap-2 text-xs rounded px-1 py-1 ${!empty && g.codes.length ? "cursor-pointer hover:bg-slate-50" : ""}`}
                   onClick={() => !empty && g.codes.length && drill({ disposition: g.codes.join(",") })}
                   data-testid={`camp-group-header-${g.key}`}>
                <div className="w-1/3 shrink-0 font-semibold text-slate-800 leading-tight">
                  {g.name}
                </div>
                <div className="flex-1 h-2.5 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${g.share}%`, backgroundColor: color }} />
                </div>
                <span className="w-12 text-right font-mono text-slate-800">{g.total}</span>
                <span className="w-12 text-right font-mono text-[10px] text-slate-400">({g.share}%)</span>
              </div>
              {g.items.map((it) => (
                <div key={it.value} className="flex items-center gap-2 text-xs rounded pl-6 pr-1 py-0.5 cursor-pointer hover:bg-slate-50"
                     onClick={() => drill({ disposition: it.value })} data-testid={`camp-group-item-${it.value}`}>
                  <div className="flex-1 min-w-0 text-slate-600 break-words leading-tight" title={it.label}>{it.label}</div>
                  <span className="w-12 text-right font-mono text-slate-700">{it.count}</span>
                  <span className="w-12" />
                </div>
              ))}
              {g.items.length === 0 && <div className="pl-6 text-[10px] text-slate-400">No dispositions in this group.</div>}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function Breakdown({ title, rows, total, testid, color }) {
  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-1.5">
        {rows.length === 0 && <div className="text-xs text-slate-400">Nothing captured yet.</div>}
        {rows.map((r) => <BarRow key={r.value} wide label={r.label} count={r.count} total={total || 1} color={color} />)}
      </CardContent>
    </Card>
  );
}
